import { useState } from 'react';
import clsx from 'clsx';

import { useKmlLayers } from '@atom/components/mapPortal/hooks/kmlHook';
import { useLayersValue } from '@atom/components/mapPortal/hooks/layersHook';
import { Checkbox, Collapse, Icon, IconButton, Switch } from '@atom/mui';
import markerUtilities from '@atom/utilities/markerUtilities';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import MapDrawerLayerIcon from './MapDrawerLayerIcon';

import classes from './mapDrawerLayers.module.css';

interface Props {
  toggled: boolean;
  onUpdate: (toggled: boolean, selected: Set<string>) => void;
}

const MapDrawerLayersKmlSection = ({ toggled, onUpdate }: Props) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const { layers: kmlLayers } = useKmlLayers();
  const { kmlLayerIds: selectedKmlLayers } = useLayersValue();

  const toggleKmlLayer = (id: string) => {
    onUpdate(toggled, toggleFromSet(selectedKmlLayers, id));
  };

  const selectAll = () => {
    onUpdate(toggled, new Set(kmlLayers.map(layer => layer.id)));
  };
  const deselectAll = () => {
    onUpdate(toggled, new Set([]));
  };

  const allSelected = kmlLayers.every(layer => selectedKmlLayers.has(layer.id));
  const noneSelected = !selectedKmlLayers.size;

  return (
    <div className={classes.sectionContainer}>
      <div className={classes.sectionHeaderContainer}>
        <Switch
          checked={toggled}
          label={
            <div className={classes.toggleLabel}>
              <div className={classes.sectionHeaderIcon}>
                <MapDrawerLayerIcon name="layers" />
              </div>
              <div>Geospatial Layers</div>
            </div>
          }
          onChange={() => {
            onUpdate(!toggled, selectedKmlLayers);
          }}
        />
        <IconButton edge="end" onClick={() => setExpanded(current => !current)}>
          <Icon>{expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
        </IconButton>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={clsx(classes.sectionFilters, classes.expanded)}>
          <div className={classes.selectAllContainer}>
            <div
              onClick={selectAll}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || allSelected) && classes.disabled,
              )}
            >
              Select All
            </div>
            <div className={classes.selectAllDivider} />
            <div
              onClick={deselectAll}
              className={clsx(
                classes.selectAllLabel,
                (!toggled || noneSelected) && classes.disabled,
              )}
            >
              Clear All
            </div>
          </div>
          {kmlLayers.map(layer => {
            const icon = markerUtilities.getKmlSvg(layer.markerId);

            return (
              <div className={classes.sectionFilterRow} key={layer.id}>
                <Checkbox
                  disabled={!toggled}
                  checked={selectedKmlLayers.has(layer.id)}
                  onChange={() => toggleKmlLayer(layer.id)}
                  edge="start"
                />
                <img style={{ height: 34, width: 34 }} src={icon} />
                <span className={classes.sectionFilterRowText}>
                  {layer.name}
                </span>
              </div>
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

export default MapDrawerLayersKmlSection;
